import { WriteQueue } from '../core/writeQueue';

/** Adds a global write barrier to the core account/workspace/conversation queue. */
export class GlobalWriteQueue {
  private readonly inFlight = new Set<Promise<unknown>>();
  private readonly writeQueue = new WriteQueue();
  private globalGate: Promise<void> = Promise.resolve();

  /** Queues a conversation write behind its core chain and the current global gate. */
  enqueue<T>(accountId: string, conversationId: string, task: () => Promise<T>): Promise<T> {
    const gate = this.globalGate;
    return this.track(
      this.writeQueue.enqueue(accountId, conversationId, async () => {
        await gate;
        return task();
      }),
    );
  }

  /** Queues an account write behind its core chains and the current global gate. */
  enqueueAccount<T>(accountId: string, task: () => Promise<T>): Promise<T> {
    const gate = this.globalGate;
    return this.track(
      this.writeQueue.enqueueAccount(accountId, async () => {
        await gate;
        return task();
      }),
    );
  }

  /** Queues a workspace write behind its core chains and the current global gate. */
  enqueueWorkspace<T>(accountId: string, workspaceId: string, task: () => Promise<T>): Promise<T> {
    const gate = this.globalGate;
    return this.track(
      this.writeQueue.enqueueWorkspace(accountId, workspaceId, async () => {
        await gate;
        return task();
      }),
    );
  }

  /** Drains submitted writes, runs one global task, and gates later writes until it settles. */
  enqueueGlobal<T>(task: () => Promise<T>): Promise<T> {
    const priorRuns = [...this.inFlight, this.globalGate];
    const run = Promise.allSettled(priorRuns).then(task);
    this.globalGate = run.then(
      () => undefined,
      () => undefined,
    );
    return this.track(run);
  }

  private track<T>(run: Promise<T>): Promise<T> {
    this.inFlight.add(run);
    void run.then(
      () => this.inFlight.delete(run),
      () => this.inFlight.delete(run),
    );
    return run;
  }
}
